// AirBridge console design tokens — FD-466 / FD-ADR-043.
//
// THE SINGLE SOURCE FOR EVERY HEX IN THE CONSOLE. If a colour is not in this
// module it does not belong in a component. `docs/RUBIX_UI_STYLE_GUIDE.md`
// carries the same table with the design reasoning; this file is what the code
// imports.
//
// It is ALSO the code-side source for the cross-repo contract (FD-ADR-038):
// `docs/design/rubix-ui-tokens.json` is published from this module and vendored
// by ATC and flight-agent. `tests/unit/docs/UiTokens.Tests.ps1` asserts the two
// agree, so the published contract cannot drift from the console that
// implements it. Change a value here and the gate tells you to republish.
//
// WHAT REPLACED WHAT, AND WHY THIS FILE KEPT ITS EXPORT NAMES
//
// FD-ADR-043 rebrands the console AirBridge: blue accent, Inter, a deep-navy
// left rail and a light top bar. The values below are the AirBridge UI Color
// System (design board, 2026-09-23). The EXPORT NAMES are unchanged — `ACCENT`,
// `SURFACE`, `STROKE`, `STATUS`, `TEXT`, `FONT` — deliberately: they are
// semantic ("the accent", "the canvas"), never "orange" or "warm", so a hundred
// call sites re-colour by changing this file rather than by being rewritten.
// That is the property the names were chosen for and it is now being spent.
//
// Two things this module deliberately is NOT:
//
// 1. It is NOT the Fluent brand ramp. Fluent controls (primary Button, checked
//    Checkbox/Switch/Radio, focus rings, Links, tinted Badges) read their colour
//    from `airbridgeTheme` in `./airbridgeTheme.ts`, applied ONCE at the root
//    provider. Never restyle a Fluent control from here — if a control looks
//    wrong, the theme token is wrong, and fixing it there is what keeps every
//    control agreeing. FD-060's hover-flicker bug came from a nested,
//    dynamically-scoped FluentProvider; do not add one.
//
// 2. It is NOT the corporate Rubix identity. The orange gradient
//    (the FFC107 -> EA7400 -> F0441D ramp; spelled without their `#` so the
//    CI grep that fails on the real thing does not have to allowlist this
//    file) is still Rubix's mark, for marketing and print. It has left the
//    product UI entirely and must not come back to it; the gate greps for it.

/**
 * Accent — AirBridge Blue.
 *
 * `base` is the CTA / link / active-tab / progress colour, used with WHITE text
 * — and white on it is **4.24:1, which FAILS WCAG AA (4.5:1)**.
 *
 * THAT IS A RECORDED OWNER DECISION (FD-ADR-043 D5), not an oversight and not a
 * value to "fix". The design board specifies `#1677F2` and the owner chose to
 * keep it exactly, with the shortfall accepted, rather than darken the ramp to a
 * compliant rung. `#0757C8` (the mark's own dark gradient stop, 6.55:1 with
 * white) was costed and declined. Do not substitute a darker blue for filled
 * buttons or link text: that would silently reverse a decision taken with the
 * arithmetic in view, which is the mirror image of the FD-388 defect — there a
 * wrong number went unchecked, here a right number must not be un-decided.
 *
 * The deviation is scoped to TEXT ON THE ACCENT. `base` as a GRAPHIC — the nav
 * indicator, a tab underline, a progress bar, a focus ring — is 4.24:1 on white
 * and 3.98:1 on the canvas, so it clears 1.4.11 on both and needs no
 * qualification.
 */
export const ACCENT = {
  /** Fluent `colorBrandBackground` / `colorCompoundBrandBackground`. */
  base: '#1677F2',
  /** Hover. */
  hover: '#1268D8',
  /**
   * Pressed, AND the accent rung for text and links — 6.61:1 on white, 6.21:1
   * on the canvas. `base` is 4.24:1 and 3.98:1, so it is the graphical rung and
   * is never a link colour. The gate asserts both halves of that split.
   */
  pressed: '#0F59BC',
  /** Tint fill behind an accent chip or a selected row. */
  tint: '#E5F1FF',
  /**
   * Sky and cyan — the mark's own gradient highlights, and chart/illustration
   * accents. GRAPHIC ONLY: neither clears 3:1 on white, so neither is ever text,
   * a border, or the sole carrier of meaning. They exist so the mark and a
   * decorative gradient can be drawn from this module rather than from a hex
   * someone pasted into a component.
   */
  highlight: '#38BDF8',
  cyan: '#22D3EE',
  /**
   * Foreground for text sitting ON `base`. White, per D5 — see above.
   *
   * Fluent controls get this through `colorNeutralForegroundOnBrand` in
   * `airbridgeTheme`; use this token for your own elements painted on the accent.
   */
  onBase: '#FFFFFF',
} as const;

/**
 * Navigation chrome — the left rail and dark panels.
 *
 * NEW IN FD-466, and it is where the shell's dark ground went. The ADR-031
 * console put a near-black bar across the top; AirBridge puts a navy rail down
 * the left and makes the top bar white. Both halves moved at once, so a dark
 * value left behind in `SURFACE.topBar` would have been a trap.
 */
export const NAV = {
  /** The persistent left rail. */
  deep: '#061B2C',
  /** Elevated dark panels and header bands. */
  raised: '#0B2942',
  /** Rail text at rest — 17.49:1 on `deep`. */
  text: '#FFFFFF',
  /** Rail secondary text and section headings — 9.89:1 on `deep`. */
  textMuted: '#B7C4D6',
  /** Fill behind the active rail row. */
  activeFill: 'rgba(255, 255, 255, 0.10)',
  hoverFill: 'rgba(255, 255, 255, 0.06)',
} as const;

/**
 * `#RRGGBB` + alpha -> `rgba()`.
 *
 * Module-local on purpose. It exists so the depth layers below can be composed
 * FROM the tokens above instead of from re-typed literals — the drift this file
 * was written to prevent. It is not exported because a component that needs a
 * translucent brand should be asking for one of the named layers below, not
 * inventing its own alpha.
 */
function rgba(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/**
 * Rail depth — FD-487.
 *
 * The rail shipped as one flat navy fill. The AirBridge board draws it with
 * three layers of depth: the ground lifts toward the brand at the top, a
 * topographic contour texture sits behind the lower half, and the active row is
 * a left-to-right brand wash rather than a flat white veil.
 *
 * SEPARATE FROM `NAV` FOR ONE REASON: every value here is a CSS gradient or an
 * rgba string, not a hex, so none of it can join the published cross-repo token
 * contract (FD-ADR-038) — that artifact carries hexes, and ATC and flight-agent
 * have no rail to put a gradient on. Keeping them in `NAV` would invite the
 * next person republishing the contract to try. Every colour they are built
 * from is a token above, so the rail still re-colours from one place.
 *
 * NONE OF IT CARRIES MEANING. It is chrome: an operator who cannot see any of
 * these layers loses nothing but depth. That is what lets the texture sit
 * behind text at all — see `TopographyTexture.tsx` for the contrast arithmetic.
 */
export const NAV_DEPTH = {
  /**
   * The rail ground. Lifts to `NAV.raised` behind the brand and settles to
   * `NAV.deep` by the first nav section, so the wordmark sits on the lighter
   * end of the rail and the texture reads against the darker one.
   *
   * The lift is small by design — `raised` is **1.17:1** against `deep` — and
   * every figure here was computed rather than estimated, which is the FD-388
   * discipline applied to a decorative layer. At the very top, white goes
   * 17.49 -> 14.89:1 and `textMuted` 9.89 -> 8.43:1, so the published
   * `nav.text` / `nav.textMuted` ratios remain the honest WORST case and the
   * contract needs no new assertion.
   */
  rail: `linear-gradient(180deg, ${NAV.raised} 0%, ${NAV.deep} 34%, ${NAV.deep} 100%)`,
  /**
   * A cool glow behind the mark. Sky rather than the accent, because this is the
   * mark's own gradient highlight and the accent rung is spoken for by the
   * active indicator a few pixels below it.
   */
  brandGlow: `radial-gradient(120% 90% at 18% 0%, ${rgba(ACCENT.highlight, 0.14)} 0%, transparent 62%)`,
  /**
   * The active row. A brand wash that falls away to the right, so the row reads
   * as lit from the indicator bar rather than as a uniform panel.
   *
   * It REPLACES `NAV.activeFill` at this one call site rather than superseding
   * it: `activeFill` is still the correct flat answer for any dark surface that
   * is not the rail, and a gradient cannot be a `backgroundColor`.
   */
  activeRow: `linear-gradient(90deg, ${rgba(ACCENT.base, 0.32)} 0%, ${rgba(
    ACCENT.base,
    0.12,
  )} 58%, ${rgba(NAV.text, 0.05)} 100%)`,
  /**
   * The active indicator bar, top to bottom: the mark's sky highlight into the
   * accent. Both rungs are graphics-only tokens and both clear 3:1 on
   * `NAV.deep`, which is all a 3px bar needs (WCAG 1.4.11).
   */
  activeIndicator: `linear-gradient(180deg, ${ACCENT.highlight} 0%, ${ACCENT.base} 100%)`,
} as const;

/** Chrome, canvas, and surfaces. */
export const SURFACE = {
  /**
   * The global top bar. LIGHT — the brand anchor and the dark ground both moved
   * to `NAV`. Its paired text tokens are ordinary ink, not a light-on-dark pair.
   */
  topBar: '#FFFFFF',
  topBarText: '#0B1F44',
  topBarTextMuted: '#52627A',
  /** The workspace canvas — a cool near-white, the AirBridge board's value. */
  canvas: '#F5F8FC',
  /** Cards, panels, table bodies. */
  base: '#FFFFFF',
  /** Zebra / metric-cell fill. */
  rowFill: '#F8FAFD',
  /** A second fill step — active row, hovered metric cell. */
  rowFillAlt: '#EEF3FA',
  /**
   * The dark band that connects a group header to its expanded micro-table.
   * Load-bearing: without it the header floats and the rows below read as an
   * unrelated table rather than as that group's contents. Now navy rather than
   * warm grey, so it belongs to the same family as the rail.
   */
  tableBand: '#0B2942',
  tableBandText: '#FFFFFF',
} as const;

// RETIRED ACCENTS, never reintroduce. sky-700 (hex 0369A1) was the pre-ADR-031
// accent; Rubix orange (hex F97316 and its ramp) was the ADR-031 accent that
// FD-ADR-043 replaced. Deliberately NOT exported as constants — a value nobody
// may use should not be importable. The literals are spelled without their `#`
// so the CI greps that fail on the real things do not have to allowlist this
// file.

/** 1px strokes. Separation comes from these and from fill steps — never shadows. */
export const STROKE = {
  /**
   * Mist. Card borders and dividers.
   *
   * 1.26:1 on white: it is a decorative divider, and it must never be the sole
   * indication of a control's bounds. Fluent's own `colorNeutralStroke1` is left
   * at its default for exactly that reason — see `airbridgeTheme.ts`.
   */
  base: '#DDE6F0',
  /** Internal divider inside an already-bordered surface. */
  subtle: '#E9EEF5',
} as const;

/**
 * Status — the whole vocabulary. A sixth meaning is not available.
 *
 * Each meaning carries THREE values and they are not interchangeable:
 *
 *   `x`        — the design-board hex. Dots, rings, bars, icons. GRAPHIC ONLY.
 *   `xTint`    — the badge / background fill.
 *   `xText`    — a DARKER rung, for the LABEL sitting on `xTint`.
 *
 * The third rung is new in FD-466 and it exists because every design-board hex
 * fails as text on its own tint (2.77–3.31:1). The `*Text` rungs clear AA on
 * their own tint (4.80–6.46:1). A pill that paints its label in the glyph hex is
 * the defect this triple prevents.
 *
 * `warning` is the sharpest case and is a published exemption rather than a
 * conformance claim: `#F5A623` is 2.03:1 on white and 1.90:1 on the canvas, so
 * it fails even the 3:1 graphics floor. A warning glyph must carry a darker
 * stroke or be drawn in `warningText`; it must never be a thin amber line on a
 * light ground.
 *
 * `success` clears 3:1 on white (3.11) and NOT on the canvas (2.92), so a status
 * dot belongs on a card, never directly on the canvas. Both facts are in the
 * contract's `knownTight` list with their rules.
 */
export const STATUS = {
  success: '#16A765',
  successTint: '#E1F7EC',
  successText: '#0E7A49',
  error: '#EF4444',
  errorTint: '#FDE8E8',
  errorText: '#B42318',
  /**
   * The operator-attention colour: a held device, a stale registry, a wave that
   * needs a human. Amber rather than red because it means "someone must act",
   * not "this failed".
   */
  warning: '#F5A623',
  warningTint: '#FFF3D8',
  warningText: '#8A5A00',
  /**
   * Underway / in-progress.
   *
   * It is a BLUE, and under AirBridge that is no longer the coincidence it would
   * have been: the accent is blue too, and "in progress" being a shade of the
   * brand is exactly what the board intends. FD-397's reason for splitting
   * underway away from the accent was that the old amber pair (#D97706 warning
   * vs the F97316 accent) were seven degrees apart and an operator triages those
   * two states by colour. Warning is now amber and the accent is blue, so the
   * collision that argument was about no longer exists.
   *
   * FLUENT SURFACES DO NOT USE THIS HEX. Keep
   * `<Badge appearance="tint" color="informative">`, whose contrast Fluent
   * handles itself. This value is for non-Fluent elements — a dot, a bar, a
   * chart segment — and for the consumers that have no Fluent to inherit.
   */
  underway: '#1683F8',
  underwayTint: '#E5F1FF',
  underwayText: '#0B5CC7',
  /** Neutral / not-applicable / no-value-yet. Never an error colour. */
  neutral: '#73839A',
  neutralTint: '#E9EEF5',
  neutralText: '#4A5568',
} as const;

/** Text neutrals. */
export const TEXT = {
  /** Headings, metric values, table emphasis — 16.23:1 on white. */
  primary: '#0B1F44',
  /** Secondary text, labels, metadata — 6.20:1 on white, 5.82:1 on the canvas. */
  secondary: '#52627A',
  /**
   * DISABLED AND INACTIVE CONTROLS ONLY — never content (FD-394).
   *
   * 2.62:1 on white. WCAG 1.4.3 exempts disabled controls, which is the only
   * reason this token is conformant at all; the exemption does not stretch to
   * anything a reader is meant to read. A label naming what a number means is
   * content, and at 11px it was failing badly.
   *
   * Legitimate: a disabled nav item, a not-yet-reached wizard step, a decorative
   * separator or an empty-state icon paired with text.
   * Not legitimate: any label, value, timestamp, identifier or form label — those
   * are `secondary`.
   *
   * FD-394 moved 15 such sites. The mistake was easy because the token reads like
   * "muted", and six of the fifteen were the same 11px uppercase micro-label
   * pattern copied between components.
   */
  disabled: '#93A1B5',
  onDark: '#FFFFFF',
} as const;

/**
 * Tailwind's preflight is DISABLED in this repo (`corePlugins.preflight: false`)
 * because Fluent owns the resets. A bare <button> therefore keeps the browser's
 * own border and background — invisible on a white surface, and a light box on
 * the navy rail. Every non-Fluent button starts from this reset and then adds
 * its own focus-visible ring so it stays keyboard-reachable.
 *
 * This is a shared constant rather than a copied string precisely because the
 * symptom is invisible in the place most components are developed.
 */
export const PLAIN_BUTTON =
  'appearance-none border-0 bg-transparent p-0 text-inherit outline-none';

/**
 * Typography — Inter, and ONLY Inter (FD-ADR-043 D2).
 *
 * Geist and Geist Mono are both retired. There is no monospace family and no
 * `mono` token: numerics use Inter with `font-variant-numeric: tabular-nums`,
 * which is what `NUMERIC` carries, and that does the one job the mono family was
 * carrying — figures that align in a column — without a second typeface.
 *
 * Inter is used INSIDE Fluent controls too, which reverses the addendum's
 * earlier two-font coexistence: the mockups render controls in the brand face,
 * so leaving Segoe UI Variable there would give buttons and inputs a different
 * voice from the page around them.
 */
export const FONT = {
  // "Inter Variable" FIRST, and it is not cosmetic. @fontsource-variable/inter
  // registers a face named 'Inter Variable' — there is no 'Inter' on that route.
  // FD-401 is the precedent: ATC followed the contract's own two fields, got
  // 'Geist' from one and the fontsource package from the other, and rendered in
  // system-ui with no error anywhere. Both spellings stay in the stack so a
  // consumer on either delivery route lands on a real face.
  display: '"Inter Variable", "Inter", system-ui, -apple-system, "Segoe UI", sans-serif',
} as const;

/** Apply to any element rendering a figure that must align in a column. */
export const NUMERIC = { fontVariantNumeric: 'tabular-nums' } as const;

/**
 * Inline identifiers — an environment id, a Manager URL, an app-setting name.
 *
 * This is what replaced `FONT.mono` when D2 retired the mono family. Those
 * sites were never *numeric*; they were short machine strings set apart from the
 * prose around them, which a monospace face did by changing typeface. Inter does
 * it with weight plus tabular figures, so an identifier still reads as a
 * quotable literal without a second family being downloaded for four spans.
 */
export const IDENTIFIER = {
  fontWeight: 500,
  fontVariantNumeric: 'tabular-nums',
} as const;

/**
 * Corner radius.
 *
 * FD-470, owner decision 2026-09-25: the AirBridge board's controls are
 * noticeably rounder than Fluent's defaults — buttons, inputs, dropdowns and
 * cards all read at roughly 8px rather than 4. This scale is mapped onto
 * Fluent's `borderRadius*` ramp in `airbridgeTheme`, so the change reaches
 * EVERY control in the console from one place rather than through per-component
 * overrides that drift.
 *
 * The scoped alternative — one rounder CTA and Fluent's defaults everywhere
 * else — was offered and declined: the board rounds its inputs and cards too,
 * so a scoped change would have left the CTA looking like a visitor on its own
 * page.
 *
 * These are geometry, not colour. The published token contract
 * (`docs/design/rubix-ui-tokens.json`) carries hexes and its parser reads only
 * `KEY: '#HEX'` pairs, so this block is invisible to the drift gate and needs no
 * contract republish.
 */
export const RADIUS = {
  /** Chips, badges, small inline affordances. */
  sm: '4px',
  /** The default: buttons, inputs, dropdowns, menu items, message bars. */
  md: '8px',
  /** Cards, panels, table surfaces. */
  lg: '10px',
  /** Dialogs and popovers — the largest surfaces, where 8px reads as square. */
  xl: '14px',
} as const;

/** Geometry the shell and its primitives agree on. */
export const LAYOUT = {
  railWidth: 240,
  topBarHeight: 56,
  contentMaxWidth: 1400,
  contextColumnWidth: 320,
} as const;

/**
 * The status vocabulary, nested — the same five meanings as `STATUS`, grouped so
 * a component can index by a state name it holds in a variable.
 *
 * `STATUS` stays flat because it is the shape the published contract overlaps
 * (`tests/unit/docs/UiTokens.Tests.ps1` parses `NAME.key: '#HEX'` and cannot see
 * into a nested literal). This view exists so `StatePill`, `Donut` and the
 * phase stepper can do `STATE[tone].soft` instead of a five-arm switch. It holds
 * NO values of its own — every field references `STATUS`, so the two cannot
 * drift.
 */
export const STATE = {
  success: { glyph: STATUS.success, soft: STATUS.successTint, text: STATUS.successText },
  progress: { glyph: STATUS.underway, soft: STATUS.underwayTint, text: STATUS.underwayText },
  warning: { glyph: STATUS.warning, soft: STATUS.warningTint, text: STATUS.warningText },
  critical: { glyph: STATUS.error, soft: STATUS.errorTint, text: STATUS.errorText },
  neutral: { glyph: STATUS.neutral, soft: STATUS.neutralTint, text: STATUS.neutralText },
} as const;

export type StateName = keyof typeof STATE;
